import { DEFAULT_LOCALE, type Locale } from "./locale";

/**
 * Legal pages use language-specific slugs (EN `/legal-notice`, DE `/de/impressum`,
 * EN `/privacy-policy`, DE `/de/datenschutz`), so switching language on one of
 * them cannot simply prefix the current path — that would 404. Each group lists
 * the full target path per locale; locales without their own page fall back to
 * the English one.
 */
type LegalGroup = Partial<Record<Locale, string>> & { en: string };

export const LEGAL_ROUTES = {
  legalNotice: { en: "/legal-notice", de: "/de/impressum" },
  privacy: { en: "/privacy-policy", de: "/de/datenschutz" },
} satisfies Record<string, LegalGroup>;

export type LegalPage = keyof typeof LEGAL_ROUTES;

const LEGAL_GROUPS: LegalGroup[] = Object.values(LEGAL_ROUTES);

/**
 * Full path of a legal page in `locale`. Locales without their own legal page
 * (e.g. PL until localized legal copy lands) fall back to the English route.
 */
export function legalHref(page: LegalPage, locale: Locale): string {
  const group: LegalGroup = LEGAL_ROUTES[page];
  return group[locale] ?? group.en;
}

/** Privacy policy path for `locale` (English fallback). */
export function privacyHref(locale: Locale): string {
  return legalHref("privacy", locale);
}

/**
 * When `href` is any locale's legal path, returns that page's path in `locale`;
 * otherwise `null`.
 */
export function resolveLegalHref(href: string, locale: Locale): string | null {
  const group = LEGAL_GROUPS.find((g) => Object.values(g).includes(href));
  return group ? (group[locale] ?? group.en) : null;
}

/**
 * Target path when switching to `locale` from `basePath` (a locale-stripped
 * pathname), or `null` when the current page is not a legal page.
 */
export function legalPathForLocale(basePath: string, locale: Locale): string | null {
  const group = LEGAL_GROUPS.find((g) =>
    Object.values(g).some((path) => path === basePath || path?.endsWith(basePath)),
  );
  if (!group) return null;
  return group[locale] ?? group.en ?? null;
}
